import React, { useContext, useEffect } from 'react'; //eslint-disable-line
import { Link } from 'react-router-dom';
import differenceInSeconds from 'date-fns/difference_in_seconds';
import { useTranslation } from 'react-i18next';

import high from '../styles/img/occupancy_high.png';
import medium from '../styles/img/occupancy_med.png';
import low from '../styles/img/occupancy_low.png';
import unknownSeat from '../styles/img/seat_icon3.png';
import goTrackerConfig from 'goTrackerConfig';
import BoltIcon from '../components/BoltIcon';
import HeaderColumns from '../components/HeaderColumns';

import utils from '../utils';
import '../i18n';
import Tags from './Tags';

const getTripRemarks = (remarks, languageName) => {
  if (!remarks || remarks.length < 1) return '';

  const remarkText = remarks.find(r => r.language === languageName);

  // eslint-disable-next-line no-nested-ternary
  return remarkText !== undefined ? remarkText.text : remarks[0] ? remarks[0].text : '';
};

const getExpectedStatus = (trip, languageName, isTwentyFourHour, t) => {
  let tripStatusMessage = t('tripStatus.onTime');
  let tripStatusClass = '';

  const tripMessages = getTripRemarks(trip.messages, languageName);

  // Display trip messages if there are any except if it is default status (On Time)
  if (trip.messages.length > 0 && tripMessages !== t('tripStatus.onTime')) {
    tripStatusMessage = tripMessages;

    if (tripMessages === t('tripStatus.cancelled')) {
      tripStatusClass = 'cancelled';
    }
  } else {
    const delayTime = differenceInSeconds(trip.actual, trip.scheduled);

    // Display delay time if it is 5 minutes or more.
    if (delayTime >= goTrackerConfig.minorDelayThreshold.t) {
      tripStatusMessage = utils.formatTime(trip.actual, isTwentyFourHour);
      tripStatusClass = 'changed';
    }
  }

  return { tripStatusMessage, tripStatusClass };
};

const getOccupancyImg = level => {
  switch (level) {
    case 'high':
      return <img src={high} alt="high"></img>;
    case 'medium':
      return <img src={medium} alt="medium"></img>;
    case 'low':
      return <img src={low} alt="low"></img>;
    default:
      return <img src={unknownSeat} alt="unknown"></img>;
  }
};

const StationDepartureRows = props => {
  const { t } = useTranslation();
  const { trips, languageName, onGetPath, isTwentyFourHour, onResetTripDetail, maxTripsDisplayed} = props;
  const headerCol = [
    t('stationDepartures.columnHeadings.1'),
    t('stationDepartures.columnHeadings.2'),
    t('stationDepartures.columnHeadings.3'),
    t('stationDepartures.columnHeadings.4'),
    t('stationDepartures.columnHeadings.5'),
  ];
  const filteredCol = !goTrackerConfig.railOccupancy ? headerCol.filter(c => c !== t('stationDepartures.columnHeadings.3')) : headerCol;
  const headerClassName = `departure-header-row ${!goTrackerConfig.railOccupancy ? 'no-occupancy' : ''}`;

  const resetTripDetail = () => {
    if (onResetTripDetail) {
      onResetTripDetail();
    }
  };

  const getPath = (trip, delayTime) => {
    if (onGetPath) {
      return onGetPath(trip, delayTime);
    }
    return {};
  };

  if (trips.tripMessages.length < 1) {
    const noTripsMessages = trips.noTripsMessage.find(m => m.language === languageName);

    const noTripText = noTripsMessages !== undefined ? noTripsMessages.text : '';

    return (
      <React.Fragment>
        <HeaderColumns className={headerClassName} headerCol={filteredCol} />
        <div className="departure-row no-trip">{noTripText}</div>
      </React.Fragment>
    );
  }

  const filteredTrips = trips.tripMessages.filter((trip, index) => index < maxTripsDisplayed);

  return (
    <React.Fragment>
      <HeaderColumns className={headerClassName} headerCol={filteredCol} />
      <ul>
        {filteredTrips.map((trip, i) => {
          const tripStatus = trip.messages.length > 0 && getTripRemarks(trip.messages) === t('tripStatus.cancelled') ? 'cancelled' : '';
          const _delayTime = differenceInSeconds(trip.actual, trip.scheduled);
          const ariaLive = i === 0 ? 'assertive' : 'off';
          const  { tripStatusMessage, tripStatusClass } = getExpectedStatus(trip, languageName, isTwentyFourHour, t);
          const path = getPath(trip, _delayTime);
          return (
            <li className="departure-row" key={trip.tripName} aria-live={ariaLive}>
              <Link to={path} role="link" onClick={resetTripDetail}>
                <div className={`data in-service remarks ${!goTrackerConfig.railOccupancy ? 'no-occupancy' : ''}`}>
                  <div aria-describedby={t('stationDepartures.columnHeadings.1')}>
                    {trip.destination} {goTrackerConfig.debug === true ? <div className="debug-container trip-number">{trip.tripName}</div> : ''}
                  </div>
                  <div className="schedule-col" aria-describedby={t('stationDepartures.columnHeadings.2')}>
                    {utils.formatTime(trip.scheduled, isTwentyFourHour)}
                    {trip.isExpress ? (
                      <span>
                        <BoltIcon />
                      </span>
                    ) : (
                      ''
                    )}
                  </div>
                  {goTrackerConfig.railOccupancy && (
                    <div className={`equipment ${trip.overallThreshold}`}>
                      {getOccupancyImg(trip.overallThreshold)}
                    </div>
                  )}
                  <div
                    className={`track ${trip.trackAttr ? trip.trackAttr.foreground : ''} ${tripStatus}`}
                    aria-describedby={t('stationDepartures.columnHeadings.4')}
                  >
                    {trip.track}
                  </div>
                  <div className={`expected ${tripStatusClass}`} aria-describedby={t('stationDepartures.columnHeadings.5')}>
                    {tripStatusMessage}
                    {<i className="fa fa-chevron-right" />}
                  </div>
                </div>
                <Tags trip={trip}/>
                {trip.remarks && trip.remarks.length > 0 && (
                  <div className={`remarks in-service ${!goTrackerConfig.railOccupancy ? 'no-occupancy' : ''}`}>
                    <p>{getTripRemarks(trip.remarks)}</p>
                  </div>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </React.Fragment>
  );
};

export default StationDepartureRows;
